// src/components/header/Header01.js

import Image from "next/image";
import Link from "next/link";
import DarkMode from "../mode/DarkMode";
import Logo from "./../../public/images/logo.png";
import WhiteLogo from "./../../public/images/logo_white.png";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import WalletButton from "../wallet-btn/WalletButton";
import { collection, doc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../../config/fireabseConfig";

export default function Header01() {
  const [toggle, setToggle] = useState(false);
  const router = useRouter();

  // State for Contribution Modal
  const [isContributeModalOpen, setIsContributeModalOpen] = useState(false);
  const [contributionData, setContributionData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    role: "",
    content: "",
  });
  const [contributionStatus, setContributionStatus] = useState({
    loading: false,
    success: null,
    error: null,
  });

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setToggle(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Navigation links
  const navigation = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Categories", path: "/categories" },
    { name: "Articles", path: "/articles" },
    { name: "HBIM Archive", path: "/archive" },
    { name: "Contact", path: "/contact" },
  ];

  // Handlers for Contribution Modal
  const openContributeModal = () => {
    setIsContributeModalOpen(true);
  };

  const closeContributeModal = () => {
    setIsContributeModalOpen(false);
    setContributionData({
      name: "",
      email: "",
      phoneNumber: "",
      role: "",
      content: "",
    });
    setContributionStatus({
      loading: false,
      success: null,
      error: null,
    });
  };

  const handleContributionChange = (e) => {
    const { name, value } = e.target;
    setContributionData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContributionSubmit = async (e) => {
    e.preventDefault();
    setContributionStatus({ loading: true, success: null, error: null });

    try {
      // Generate a new document reference with a unique ID
      const docRef = doc(collection(db, "contributions"));
      const id = docRef.id;

      // Prepare the data according to the Contribution interface
      const contribution = {
        id,
        name: contributionData.name,
        email: contributionData.email,
        phoneNumber: contributionData.phoneNumber,
        role: contributionData.role,
        content: contributionData.content,
        createdAt: Timestamp.now(),
        // updatedAt is optional and can be set when updating the document
      };

      // Set the document with the prepared data
      await setDoc(docRef, contribution);

      setContributionStatus({
        loading: false,
        success: "Your contribution has been submitted successfully!",
        error: null,
      });

      // Optionally, close the modal after a delay
      setTimeout(() => {
        closeContributeModal();
      }, 3000);
    } catch (error) {
      console.error("Error adding document: ", error);
      setContributionStatus({
        loading: false,
        success: null,
        error: "Failed to submit your contribution. Please try again.",
      });
    }
  };

  return (
    <>
      {/* Desktop Header */}
      <header className="fixed top-0 z-20 w-full backdrop-blur transition-colors">
        <div className="flex items-center justify-between px-6 py-6 xl:px-24">
          {/* Logo */}
          <Link href="/">
            <a>
              <div className="dark:hidden">
                <Image
                  src={Logo}
                  height={56} // 28 * 2
                  width={260} // 130 * 2
                  alt="gmja | Ethiopian Architecture"
                  className="max-h-14 h-auto"
                />
              </div>
              <div className="hidden dark:block">
                <Image
                  src={WhiteLogo}
                  height={56} // 28 * 2
                  width={260} // 130 * 2
                  alt="gmja | Ethiopian Architecture"
                />
              </div>
            </a>
          </Link>
          {/* End Logo */}

          {/* Placeholder for alignment */}
          <div className="lg:flex basis-3/12"></div>
          {/* End Placeholder */}

          {/* Navigation Links */}
          <nav className="hidden lg:flex space-x-8">
            {navigation.map((navItem) => (
              <Link key={navItem.name} href={navItem.path}>
                <a
                  className={`text-jacarta-700 font-display hover:text-accent focus:text-accent dark:text-white ${
                    router.pathname === navItem.path
                      ? "text-accent dark:text-accent border-2 border-accent rounded-full px-3 py-1"
                      : ""
                  } transition-all`}
                >
                  {navItem.name}
                </a>
              </Link>
            ))}
          </nav>
          {/* End Navigation Links */}

          {/* Header Right Content */}
          <div className="hidden lg:flex items-center space-x-4 ml-auto">
            {/* Wallet Button */}
            {/* <WalletButton /> */}
            {/* Contribute Button */}
            <button
              onClick={openContributeModal}
              className="bg-green-500 px-4 py-2 rounded-md border-jacarta-600 border-2 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400"
            >
              Contribute 💡
            </button>
            {/* Dark Mode Toggle */}
            <DarkMode />
          </div>
          {/* End Header Right Content */}

          {/* Mobile Menu Toggle */}
          <div className="flex lg:hidden items-center space-x-2">
            {/* Wallet Button */}
            {/* <WalletButton /> */}
            {/* Dark Mode Toggle */}
            <DarkMode />
            <button
              className="flex items-center justify-center h-10 w-10 rounded-full bg-white dark:bg-white/[.15] border border-jacarta-100 dark:border-transparent transition-colors hover:bg-accent focus:bg-accent"
              onClick={() => setToggle(true)}
              aria-label="Open mobile menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width={24}
                height={24}
                className="fill-jacarta-700 h-6 w-6 dark:fill-white"
              >
                <path fill="none" d="M0 0h24v24H0z" />
                <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z" />
              </svg>
            </button>
          </div>
          {/* End Mobile Menu Toggle */}
        </div>
      </header>
      {/* End Desktop Header */}

      {/* Mobile Menu */}
      {toggle && (
        <div className="fixed inset-0 z-30 bg-white dark:bg-jacarta-800 p-6 lg:hidden">
          <div className="flex items-center justify-between mb-8">
            <Link href="/">
              <a>
                <div className="dark:hidden">
                  <Image
                    src={Logo}
                    height={56} // 28 * 2
                    width={260} // 130 * 2
                    alt="gmja | Ethiopian Architecture"
                    className="max-h-14 h-auto"
                  />
                </div>
                <div className="hidden dark:block">
                  <Image
                    src={WhiteLogo}
                    height={56} // 28 * 2
                    width={260} // 130 * 2
                    alt="gmja | Ethiopian Architecture"
                  />
                </div>
              </a>
            </Link>
            <button
              className="flex items-center justify-center h-10 w-10 rounded-full bg-white dark:bg-white/[.15] border border-jacarta-100 dark:border-transparent transition-colors hover:bg-accent focus:bg-accent"
              onClick={() => setToggle(false)}
              aria-label="Close mobile menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width={24}
                height={24}
                className="fill-jacarta-700 h-6 w-6 dark:fill-white"
              >
                <path fill="none" d="M0 0h24v24H0z" />
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Mobile Navigation Links */}
          <nav className="space-y-6">
            {navigation.map((navItem) => (
              <Link key={navItem.name} href={navItem.path}>
                <a
                  className={`block text-jacarta-700 font-display text-lg hover:text-accent focus:text-accent dark:text-white ${
                    router.pathname === navItem.path
                      ? "text-accent dark:text-accent border-2 border-accent rounded-full px-3 py-1"
                      : ""
                  } transition-all`}
                  onClick={() => setToggle(false)}
                >
                  {navItem.name}
                </a>
              </Link>
            ))}
            {/* Contribute Button in Mobile Menu */}
            <button
              onClick={() => {
                openContributeModal();
                setToggle(false);
              }}
              className="block w-full text-left text-green-500 font-display text-lg font-semibold hover:text-green-600 focus:text-green-600"
            >
              Contribute 💡
            </button>
          </nav>
          {/* End Mobile Navigation Links */}
          {/* Additional Mobile Content (e.g., Wallet, DarkMode) */}
          <div className="mt-8 flex items-center space-x-4">
            {/* <WalletButton /> */}
            <DarkMode />
          </div>
        </div>
      )}
      {/* End Mobile Menu */}

      {/* Contribution Modal */}
      {isContributeModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
          onClick={closeContributeModal}
        >
          <div
            className="bg-white dark:bg-jacarta-700 rounded-lg p-8 w-11/12 max-w-lg shadow-lg relative overflow-y-auto max-h-screen"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
          >
            {/* Close Button */}
            <button
              onClick={closeContributeModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
              aria-label="Close modal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <h2 className="text-2xl font-semibold mb-6 text-jacarta-700 dark:text-white">
              Contribute
            </h2>
            <form onSubmit={handleContributionSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label
                  htmlFor="contributionName"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="contributionName"
                  name="name"
                  required
                  value={contributionData.name}
                  onChange={handleContributionChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-accent focus:ring-accent dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                />
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="contributionEmail"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="contributionEmail"
                  name="email"
                  required
                  value={contributionData.email}
                  onChange={handleContributionChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-accent focus:ring-accent dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                />
              </div>

              {/* Phone Number */}
              <div>
                <label
                  htmlFor="contributionPhoneNumber"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="contributionPhoneNumber"
                  name="phoneNumber"
                  required
                  value={contributionData.phoneNumber}
                  onChange={handleContributionChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-accent focus:ring-accent dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                />
              </div>

              {/* Role */}
              <div>
                <label
                  htmlFor="contributionRole"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Role
                </label>
                <select
                  id="contributionRole"
                  name="role"
                  required
                  value={contributionData.role}
                  onChange={handleContributionChange}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-white shadow-sm focus:border-accent focus:ring-accent dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                >
                  <option value="">Select your role</option>
                  <option value="Student">Student</option>
                  <option value="Employee">Employee</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Content */}
              <div>
                <label
                  htmlFor="contributionContent"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Content
                </label>
                <textarea
                  id="contributionContent"
                  name="content"
                  required
                  value={contributionData.content}
                  onChange={handleContributionChange}
                  rows="4"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-accent focus:ring-accent dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                ></textarea>
              </div>

              {/* Submission Status */}
              {contributionStatus.success && (
                <div className="text-green-600 text-sm text-center">
                  {contributionStatus.success}
                </div>
              )}
              {contributionStatus.error && (
                <div className="text-red-600 text-sm text-center">
                  {contributionStatus.error}
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={closeContributeModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 dark:bg-jacarta-600 dark:text-gray-300 dark:hover:bg-jacarta-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={contributionStatus.loading}
                  className={`px-4 py-2 bg-lime-800 text-white rounded hover:bg-accent-dark focus:outline-none ${
                    contributionStatus.loading
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {contributionStatus.loading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
            {/* Confirmation Message */}
            {contributionStatus.success && (
              <p className="mt-4 text-center text-green-600">
                Dear User, thank you for your contribution! We will review your
                request and get back to you shortly.
              </p>
            )}
          </div>
        </div>
      )}
      {/* End Contribution Modal */}
    </>
  );
}
